import { Component, Vue, Watch } from "vue-facing-decorator";
import Bubble from "../components/bubble.vue";
import smoothscroll from "smoothscroll-polyfill";
import { __vueEvent, __oneMinute } from "../constant";
import { EVENTS } from "../enums/events";
import { IConfig } from "../interfaces/config";
import Loader from "../components/loader.vue";
import QuickReply from "../components/quick_reply.vue";
import TypingIndicator from "../components/typing_indicator.vue";
import ProcessingMessage from "../components/processing_message.vue";
import { lazyComponent } from "../helpers/lazy_component";
// Lazy-loaded: only needed behind an explicit user action or a config flag,
// never on the initial render path (SD-13404 phase 3).
const LoginModal = lazyComponent(() => import("../components/login_modal.vue"));
const TranscriptModal = lazyComponent(() => import("../components/footer-buttons/transcript_modal.vue"));
const ConsentModal = lazyComponent(() => import("../components/consent_modal.vue"));
const Modal = lazyComponent(() => import("../components/modal.vue"));
import Card from "../components/card.vue";
import { STORE_ACTION, STORE_MUTATION, CACHE_ID_TYPE, MESSAGE_STRUCTURE_TYPE, MESSAGE_TYPE, CONSTANTS } from "../enums";
import { IMessage } from "../interfaces";
import { ChatService } from "../service/chat_service";
import { clearProcessingTimeout, getMessageFromServer, sanitizeHtml, sanitizeToText } from "../helpers";
import Banner from "../components/banner.vue";
import { isMobile } from "../helpers/is_mobile";
import { Util } from "../util";
import { FeedbackRating, MessageContentSettings, MessageStructure, ScrollPosition } from '../types';
import LayoutCarousel from "../components/layout-carousel.vue";
import MessageFeedback from "../components/chat/message_feedback.vue";
const FeedbackModal = lazyComponent(() => import("../components/chat/feedback_modal.vue"));
import { TimeoutId } from "../types/components/transcript";
import { DynamicAccessibilityHandler } from "../helpers/accessibility/dynamic_accessibility_handler";
import { toggleInertForSiblings } from "../helpers/a11y/utils";
import { isVisible, query, queryLast, nextMatching } from "../helpers/dom";
import { UrlUtils } from "../helpers/inline-actions/utils/url_utils";
import { AvatarDisplayMode, ResolvedSenderSettings, SenderDisplayMode, SenderDisplaySettingsConfig } from "../types/sender_display";
import { handleSpecialIframeUrl } from "../helpers/action_handler";

@Component({
  components: {
    Bubble,
    Loader,
    QuickReply,
    TypingIndicator,
    Card,
    Banner,
    ProcessingMessage,
    Modal,
    LoginModal,
    TranscriptModal,
    ConsentModal,
    LayoutCarousel,
    MessageFeedback,
    FeedbackModal
  },
})
export default class Chat extends Vue {
    //#region Class properties
    //isUserHasAvatar = false;

    isPageLoaded = false;

    hideTagLine = false;

    isPrevMessageFetching = false;
    /**
     * JIRA SD-10864: Holds the current message to be announced by the screen reader.
     * Tied to a visually hidden aria-live region to support macOS VoiceOver.
     */
    a11yAnnouncement: string = "";
    isWelcomeRead = false
    isTitleRead = false
    
    screenReaderTimeout: TimeoutId | null = null;
    screenReaderMessageTimeout: TimeoutId | null = null;
    streaminMessageTimeout: TimeoutId | null = null;
    inertInterval: NodeJS.Timeout | null = null;

    anchorScrollOffset = 0;
    resetSpacer = true;

		// Feedback modal state
		showFeedbackModal = false;
		feedbackModalData: any = null;
    
    showScrollToBottom = false;
    isManualScrollingToBottom = false;
    
		recoveryScrollSuppressCount = 0;
		readonly RECOVERY_EXPECTED_SCROLL_EMITS = 2;

		//#endregion

		//#region Computed Properties

    get isMobileActive() {
      return isMobile.any() || this.$store.state.isIFrameLoaded;
    }

    get showTypingIndicator() {
      return this.$store.state.showTypingIndicator || false;
    }

    get processingDisplayText() {
      return this.$store.state.processingDisplayText || false;
    }

    get messages() {
      return this.$store.state.messages || [];
    }

    get isBotHasAvatar() {
      return this.$store.state.isBotHasAvatar || false;
    }

    get shouldShowBanner() {
      return this.$store.state.shouldShowBanner || false;
    }

    get isUserHasAvatar() {
      return this.$store.state.isUserHasAvatar || false;
    }

    get showProcessingMessage() {
      return this.$store.state.showProcessingMessage || false;
    }

    get showAdminLoginModal() {
      return this.$store.state.showAdminLoginModal || false;
    }

    get showConsentModal() {
      return this.$store.state.showConsentModal || false;
    }

    get showWelcomeBanner() {
      return this.$store.state.showWelcomeBanner || false;
    }

    get enableMessageNavigation() {
      return this.$store.state.enableMessageNavigation || false;
    }

    get showTranscriptModal() {
      return this.$store.state.showTranscriptModal || false;
    }

    get screenReaderAnnouncement() {
      return this.$store.state.screenReaderAnnouncement || "";
    }

    get isStreamingMessages() {
      return this.$store.state.isStreamingMessages || false;
    }

    get lastStreamedMessageCount() {
      const lastMessage = this.messages[this.messages.length - 1];
      return lastMessage?.contents?.keys?.streamingMessageId ? lastMessage?.contents?.structures?.[0]?.components?.length : 0
    }
    
    /**
     * Controls the 'inert' state of the main application container.
     * When true, the background content is non-interactive (non-clickable, non-tabbable),
     * effectively trapping focus within an active modal or dialog.
     * * @type {boolean} - True if a modal is currently visible.
     */
    get isModalVisible() {
      return !!(this.$store.state.showAdminLoginModal || this.$store.state.showConsentModal || this.$store.state.showTranscriptModal || this.showFeedbackModal || this.$store.state.carouselModalDetail || this.$store.state.carouselModalList)
    }
    
    get isPopupIframe(): boolean {
      return this.$store.state.isIFrameLoaded;
    }

    //#endregion

    mounted() {
        // initiate scroll polyfill
        smoothscroll.polyfill();
        this.listenEvents();
    }

    updated() {
        if (this.$store.state.isIFrameLoaded) {
            this.$nextTick(function () {
                this.trapFocus();
            });
        }
    }

    /**
     * SD-5153: Returns the message prefix based on the given message type.
     *
     * @param messageType - The type of the message.
     * @returns The message prefix.
     */
    getMessagePrefix(messageType: MESSAGE_TYPE): string {
        try {
            switch (messageType) {
                case MESSAGE_TYPE.Bot:
                case MESSAGE_TYPE.Agent:
                    return 'Bot says: ';
                case MESSAGE_TYPE.User:
                    return 'I say: ';
                // case MESSAGE_TYPE.Agent:
                //     return 'Agent said: ';
                default:
                    return ''; // Handle other message types as needed
            }
        } catch (error) {
            //console.error('Error occurred:', error);
            return '';
        }
    }

    trapFocus() {
        var element = <HTMLElement>document.getElementsByClassName(`${this.$store.state.prefixClassName}standalone`)[0];

        //Last focusable element of popup
        var lastFocusableEl;
        if (this.$store.state.disableInput) {
            var focusableEls = element.querySelectorAll(`button:not([style*="display:none"]):not([style*="display: none"])`);
            var displayedFocusElement = [];
            for (var i = 0; i < focusableEls.length; i++) {
                if (isVisible(focusableEls[i])) {
                    displayedFocusElement.push(focusableEls[i]);
                }
            }
            lastFocusableEl = <HTMLElement>displayedFocusElement[displayedFocusElement.length - 1];
        } else {
            // SD-13404-followup: resolve to the true last visible focusable button in the
            // footer (send, or whichever of lang/transcript/info/camera is last-shown) -
            // hardcoding to send here skipped the footer action buttons entirely, since
            // Tab from send always matched this "last focusable" check and redirected to
            // the popup close button before those buttons were ever reachable.
            lastFocusableEl = queryLast(`#satisfiFooterContainer button`, true)
                ?? query(`button.${this.$store.state.prefixClassName}btn.${this.$store.state.prefixClassName}send`);
        }
        this.$store.commit(STORE_MUTATION.SetLastFocusableElement, lastFocusableEl);
    }

    lastProgrammaticScrollTime: number = 0;
    lastScrollTop: number = 0;
    scrollIndicatorTimeout: any = null;

    listenEvents() {
        //@ts-ignore
        __vueEvent.on(EVENTS.ScrollChatToBottom, this.scrollChatToBottom);
        __vueEvent.on(EVENTS.ScrollChatToTop, this.scrollChatToTop);
        __vueEvent.on(EVENTS.SetScrollSpacerHeight, this.setScrollSpacerHeight);
        //@ts-ignore
        __vueEvent.once(EVENTS.SetConfig, (config: IConfig) => {
                this.isPageLoaded = true;
                this.hideTagLine = config.hideTagLine;
            });
        __vueEvent.on(EVENTS.ScrollToNewMessageTop, this.scrollToNewMessageTop);
        __vueEvent.on(EVENTS.ScrollFirstAgentToAnchor, this.scrollFirstAgentToAnchor);
        __vueEvent.on(EVENTS.ScrollTopToLastUserMessage, this.scrollTopToLastUserMessage);

        // Add feedback modal event listener
        __vueEvent.on(EVENTS.ShowFeedbackModal, this.handleShowFeedbackModal);
        __vueEvent.on(EVENTS.HideFeedbackModal, this.handleHideFeedbackModal);

        const fetchMessage = async () => {
            const element = this.chatBody; //(event.srcElement as HTMLElement)
            const previousScrollHeightMinusTop =
                element.scrollHeight - element.scrollTop;
            const msgs = await getMessageFromServer(
                this.$store.state.lastMessageId,
                CACHE_ID_TYPE.BeforeMessageCacheId,
                true,
                this.$store.state.minimumClientVersion || "",
                `chat.ts:fetchMessage`
            );
            setTimeout(() => {
                this.$store.dispatch(STORE_ACTION.AddPrevMessages, msgs);
                this.isPrevMessageFetching = false;
                setTimeout(() => {
                    if (msgs.length > 0) {
                        element.scrollTop =
                            element.scrollHeight - previousScrollHeightMinusTop;
                    } else {
                        element.scrollTop = 0;
                    }
                }, 0);
            }, 500);
        };
        this.chatBody.onscroll = async event => {
            const element = event.srcElement as HTMLElement;
            const scrollTop = element.scrollTop;
            let currentScrollHeight = element.scrollHeight;

            const scrollDelta = Math.abs(scrollTop - this.lastScrollTop);
            this.lastScrollTop = scrollTop;

            // Reduce anchor-scroll-spacer height when user scrolls up or down (smoothly)
            let differenceFromBottom = currentScrollHeight - element.clientHeight - scrollTop;
            const isUserScroll = Date.now() - this.lastProgrammaticScrollTime > 1500;
            if (isUserScroll && scrollDelta > 0 && this.anchorScrollOffset > 0 && !this.resetSpacer) {
                const amountToReduce = Math.min(scrollDelta, this.anchorScrollOffset);
                this.anchorScrollOffset -= amountToReduce;
                
                // Adjust currentScrollHeight for the rest of this function so it reflects the upcoming DOM update
                currentScrollHeight -= amountToReduce;
                differenceFromBottom -= amountToReduce;
            }

            const threshold = 25; // Show button if not at the bottom
            const isAtBottom = differenceFromBottom <= threshold;
            
            var parent = document.getElementById("divChatBody");
            let totalSectionHeight = 0;

            parent.querySelectorAll("section").forEach(section => {
                totalSectionHeight += section.offsetHeight;
            });
            const hasContent = parent.clientHeight < totalSectionHeight;
            
            if (this.scrollIndicatorTimeout) {
                clearTimeout(this.scrollIndicatorTimeout);
                this.scrollIndicatorTimeout = null;
            }

            if (isUserScroll) {
                this.showScrollToBottom = hasContent && !isAtBottom;
            } else {
                this.scrollIndicatorTimeout = setTimeout(() => {
                    if (this.$refs.chat_body) {
                        const el = this.chatBody;
                        const isNowAtBottom = (el.scrollHeight - el.clientHeight - el.scrollTop) <= threshold;
                        this.showScrollToBottom = hasContent && !isNowAtBottom;
                    }
                }, 500);
            }
            
            if (
                this.isPrevMessageFetching === false &&
                scrollTop <= 10 &&
                this.$store.state.lastMessageId > 0
            ) {
                this.isPrevMessageFetching = true;
                const previousScrollHeightMinusTop =
                    currentScrollHeight - scrollTop;
                //this.$store.commit(STORE_MUTATION.AddFetchMsgStamp, "Loading previous messages");
                setTimeout(() => {
                    element.scrollTop = 0; //element.scrollHeight - previousScrollHeightMinusTop;
                }, 0);
                await fetchMessage();
            }
        };
        
        __vueEvent.on(EVENTS.ClearProcessingTimeout, () => { clearProcessingTimeout(); });
    }


    /**
     * The function takes a string representing a date and returns a Date object,
     * or the current date if the input is invalid.
     * @param {string} dateString - The dateString parameter is a string that represents a date.
     * @returns a Date object.
     */
    convertStringToDate(dateString: string) {
        let date = null;
        try {
            if (!dateString) {
                throw new Error("Input date string is empty or null.");
            }
            const dateObject = new Date(dateString);
            if (isNaN(dateObject.getTime())) {
                throw new Error("Invalid date representation.");
            }
            date = dateObject;
        } catch (ex) {
            date = null;
        }
        return date;
    }

    // Function to serialize the date object to a string
    serializeDate(date) {
        try {
            if (!date || isNaN(new Date(date).getTime())) {
              return ""; // Return an empty string if the date is empty, false, or not a valid Date object
            }

            return new Date(date).toISOString(); // Convert the date to an ISO 8601 string (e.g., "2023-07-01T12:34:56.789Z")
        } catch (error) {
            //console.error("Error occurred during date serialization:", error);
            return ""; // Return an empty string if there was an error during the conversion
        }
    }
    // SD-13787: admin mode restore moved to helpers/admin_mode.ts, started from ConfigLoader.

    get chatBody() {
        return this.$refs.chat_body as HTMLElement;
    }

    /**
     * Gets the indicator text displayed when a message is being sent.
     * @returns The text to display while sending a message,
     * defaulting to a constant if not specified in page configuration.
     */
    get sendingIndicatorContent(): string {
      const pageConfig = this.$store.state.pageConfig;
      const sendingIndicatorText = pageConfig ? pageConfig.sendingIndicatorText : "";
      return sendingIndicatorText || CONSTANTS.SENDING_MESSAGE;
    }

		//#region Handle Transcript Modal Change

		/**
		 * The function returns a class name by concatenating a prefix with a given value.
		 * @param value - The value parameter is a string representing the class name
		 * @returns the concatenation of the value parameter and the prefixClassName property of the
		 * store.state object.
		 */
		getClassName(value) {
			try {
				return this.$store.state.prefixClassName + value;
			} catch (ex) {
				return value;
			}
		}

		@Watch("showTranscriptModal", { immediate: true })
		onPropertyChanged(value: any) {
			this.handleShowTranscriptModalChange(value);
		}

		/**
		 * SD-8649: Watches for changes in the visibility of the admin login modal.
		 */
		@Watch("showAdminLoginModal", { immediate: true })
		onAdminLoginModalChanged(value: any) {
			this.handleShowTranscriptModalChange(value);
		}

		/**
		 * SD-8438: Handles the change in visibility of the transcript modal.
		 *
		 * This method manages the addition or removal of a modal-specific CSS class to the document body
		 * based on whether the transcript modal is being shown or hidden. This behavior is specifically
		 * targeted at Safari browsers, iOS devices with WKWebView, or specific iOS scenarios where the
		 * application is not running in standalone mode or Safari.
		 *
		 * @param value - A boolean or truthy/falsy value indicating whether the transcript modal is being shown (true) or hidden (false).
		 */
		handleShowTranscriptModalChange(value: any) {
			try {
				let userAgentString = navigator.userAgent.toLowerCase();
				let safariIndex = userAgentString.indexOf("safari");

				// Check if the chat page is opened in an iOS WK Webview
				const standalone = "standalone" in navigator && navigator["standalone"];
				const safari = /safari/.test(userAgentString);
				const ios = /iphone|ipod|ipad|macintosh/.test(userAgentString);

				// SD-10057: Add the modal-show class to all normal browsers and iOS WK Webview
				if ((document.body != null) || (ios && !standalone && !safari)) {
					// If the modal is shown, add the class to the body
					if (value) {
						document.body.classList.add(this.getClassName("modal-show"));
					} else {
						// If the modal is hidden, remove the class from the body
						if (document.body.classList.contains(this.getClassName("modal-show"))) {
							document.body.classList.remove(this.getClassName("modal-show"));
						}
					}
				}
			} catch (ex) {
				console.log("[chat.ts] An error has occurred -> Inside handleShowTranscriptModalChange -> Error: ", ex);
			}
		}

		//#endregion

    /**
     * Normalizes the scroll position value to ensure it is one of the accepted values.
     */
    normalizeScrollPosition(scrollPosition: unknown): ScrollPosition {
        switch (scrollPosition) {
            case "top":
            case "near_top":
            case "default":
                return scrollPosition;
            default:
                return "default";
        }
    }

    /**
     * Scrolls to a specific anchor position in the chat body based on the provided offset and scroll position.
     */
    async scrollToAnchorPosition(targetOffsetTop: number, scrollPosition: unknown) {
        try {
            if(scrollPosition == "no_scroll"){
                return;
            }
            
            const normalizedScrollPosition = this.normalizeScrollPosition(scrollPosition);
            if (normalizedScrollPosition !== "top" && normalizedScrollPosition !== "near_top") {
                this.resetSpacer = true;
                return;
            }

            const element = this.chatBody;
            if (!element) {
                this.resetSpacer = true;
                return;
            }

            const nearTopHeight = document.getElementById("divChatBody").clientHeight * 0.25;
            const modeOffset = normalizedScrollPosition === "near_top" ? nearTopHeight : 6;
            const desiredTop = Math.max(targetOffsetTop - modeOffset, 0);
            //const maxScroll = Math.max(element.scrollHeight - element.clientHeight, 0);
            this.resetSpacer = false;
            this.anchorScrollOffset = document.getElementById("divChatBody").clientHeight;
            this.lastProgrammaticScrollTime = Date.now();

            //const isIFrameLoaded = this.$store.state.isIFrameLoaded;
            //const isTilesOpened = this.$store.state.isTilesOpenedInIframe;
            
            // Yes, the current browser supports native smooth scroll
            setTimeout(() => {
              if (element != null) {
                // Check if the current browser is Mac Computers or iOS Devices
                if (!this.isMacLike()) {
                  element.scroll({ top: desiredTop, behavior: "smooth" });
                } else {
                  // Current device is an iMac, Macbook, iPhone, iPod, or iPad
                  this.scrollToElement(element, desiredTop, 200);
                }
              }
            }, 10);
            
            // Android-only path
            if (isMobile.Android() && !this.isMacLike()) {
              await Util.nextFrame();
              await Util.smoothScrollAndVerify(element, desiredTop);
            }
        } catch (ex) {
            this.resetSpacer = true;
        }
    }

    /**
     * Scrolls the chat to the anchor of the first agent message after the latest user message.
     */
    scrollFirstAgentToAnchor(payload: { scrollPosition?: unknown } = {}) {
      try {
        const normalizedScrollPosition = this.normalizeScrollPosition(payload.scrollPosition);
        if (normalizedScrollPosition !== "top" && normalizedScrollPosition !== "near_top") {
          this.resetSpacer = true;
          return;
        }

        let offsetTop = 0;
        const lastUserMessageElem = nextMatching(
          queryLast(".satisfi_messages section.satisfi_message.satisfi_user", true),
          ".satisfi_bot"
        );
        if (lastUserMessageElem != null) {
          offsetTop = lastUserMessageElem.offsetTop;
        }

        this.scrollToAnchorPosition(offsetTop, normalizedScrollPosition);
      } catch (ex) {
        this.resetSpacer = true;
      }
    }

    scrollChatToTop() {
        this.isPrevMessageFetching = true;
        this.lastProgrammaticScrollTime = Date.now();
        setTimeout(() => {
            const element = this.chatBody;
            this.resetSpacer = true;
            element.scroll({ top: 0 });
        }, 10);
    }

    /**
     * Detect if the system is Mac Computers or iOS Devices
     * like iMac, Macbook, iPhone, iPod, or iPad.
     * @returns {boolean} true if the system is Mac Computers or iOS Devices, false otherwise.
     */
    isMacLike() {
        try {
            const userAgent = navigator.userAgent || 'unknown';
            const appleDeviceRegex = /Mac|iPhone|iPod|iPad/i;
            const isMacLike = appleDeviceRegex.test(userAgent);
            return isMacLike;
        } catch (ex) {
            return false;
        }
    }

    /**
     * Scroll to the destination element by the duration specified.
     * @param {HTMLElement} scrollLayer - HTMLElement - The element that you want to scroll.
     * @param {number} destination - The position to scroll to.
     * @param {number} duration - The duration of the scroll animation in milliseconds.
     */
    scrollToElement(scrollLayer: HTMLElement, destination: number, duration: number) {
        try {
            if (duration <= 0) return;
            const difference = destination - scrollLayer.scrollTop;
            const perTick = (difference / duration) * 10;

            setTimeout(() => {
                scrollLayer.scrollTop = scrollLayer.scrollTop + perTick;
                if (scrollLayer.scrollTop === destination) return;
                this.scrollToElement(scrollLayer, destination, duration - 10);
            }, 10);
        } catch (ex) {
            return false;
        }
    }

    isElementVisible(container, element) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        return (
            elementRect.top >= containerRect.top &&
            elementRect.bottom <= containerRect.bottom
        );
    }
    
    scrollToNewMessageTop() {
		try {
      this.lastProgrammaticScrollTime = Date.now();
      
      if(!(this.$store.state.isStreamingPages  && this.$store.state.incomingScrollPosition == "default"))
      {
        this.resetSpacer = true;
      }
			

			// detect support for the behavior property in ScrollOptions
			const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;

			// SD-1671
			// Fix for Tiles Scrolling Issue In Chat
			// 1. Check if the chat is loaded inside the iframe
			const isIFrameLoaded = this.$store.state.isIFrameLoaded;
			const isTilesOpened = this.$store.state.isTilesOpenedInIframe;

			// Yes, the current browser supports native smooth scroll
			setTimeout(() => {
				const element = this.chatBody;
				if (element != null) {

					// Get the offsetTop value for the last message
					let offsetTop = 0;
					try {
						let lastMessageElem = queryLast(".satisfi_messages section.satisfi_message", true);
            if (lastMessageElem) {
              if(this.isElementVisible(element, lastMessageElem)) {
                return;
              }
            }

						// Check if an element exists in the visible DOM
						if (lastMessageElem) {
							offsetTop = lastMessageElem.offsetTop;
						}
					} catch (ex2) {
						offsetTop = 0;
					}

					// SD-2838
                    // Check if the current browser is Mac Computers or iOS Devices
                    if (!this.isMacLike()) {
						// Check if this page is inside an iframe and also a Tile
						if (!!isIFrameLoaded && !!isTilesOpened) {
							// Fix for scrollIntoView() causing the whole page to move, when opened inside a Tile
							element.scroll({ top: offsetTop, behavior: "smooth" });
						} else {
							// continue with default behavior

                            // SD-3057
                            // Fix for Webchat UI Issue when Grammarly extension is installed
							queryLast(".satisfi_messages section")?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'start'
                            });
						}
					} else {
						// Using `true` here
						// as Safari supports scrollIntoView with boolean parameter, but not smooth behavior option
						//jQuery(".satisfi_messages section:last")[0].scrollIntoView(true);

                        // Current device is an iMac, Macbook, iPhone, iPod, or iPad
                        // custom function for iOS
                        this.scrollToElement(element, offsetTop, 200);
					}
          element.style.height = "500px";
				}
				
			}, 10);

		} catch (ex) {
			//console.log('Error: ', (ex as Error).message);
		}
    }
    
    /**
     * SD-11816: Determines whether to suppress auto-scrolling based on the current state 
     * and counts the number of suppressed scroll events.
     */
    shouldSuppressScrollAndReset(): boolean {
      try {
        // If auto-scroll suppression is enabled, count the scroll events and disable suppression
        // This ensures that auto-scroll is only suppressed for the initial scroll events 
        // triggered during message recovery (e.g., after a signalR reconnection at `fetchFailedMessages` function),
        // and is re-enabled afterwards to allow normal auto-scrolling behavior.
        //console.log("\n\n")
        //console.log("[SCROLL] suppressNextScroll =", this.$store.state.suppressNextScroll);
        if (!this.$store.state.suppressNextScroll) return false;
        
        this.recoveryScrollSuppressCount += 1;
        //console.log("[SCROLL] suppressed emit count =", this.recoveryScrollSuppressCount);

        if (this.recoveryScrollSuppressCount >= this.RECOVERY_EXPECTED_SCROLL_EMITS) {
          this.recoveryScrollSuppressCount = 0;
          this.$store.commit(STORE_MUTATION.SetSuppressNextScroll, false);
          //console.log("[SCROLL] suppressNextScroll set to false, count reset to 0");
        }
        
        //console.log("[SCROLL] return (suppressed)");
        return true;
      } catch (ex) {
        //console.error("Error in shouldSuppressScrollAndReset:", ex);
        return false; // do not block scroll if guard logic breaks.
      }
    }

    scrollTopToLastUserMessage() {
		try {
			this.lastProgrammaticScrollTime = Date.now();

			// detect support for the behavior property in ScrollOptions
			const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;

			// SD-1671
			// Fix for Tiles Scrolling Issue In Chat
			// 1. Check if the chat is loaded inside the iframe
			const isIFrameLoaded = this.$store.state.isIFrameLoaded;
			const isTilesOpened = this.$store.state.isTilesOpenedInIframe;

			// Yes, the current browser supports native smooth scroll
			setTimeout(() => {
				const element = this.chatBody;
				if (element != null) {

					// Get the offsetTop value for the last message
					let offsetTop = 0;
					try {
						let lastMessageElem = queryLast(".satisfi_messages section.satisfi_message.satisfi_user", true);
						// Check if an element exists in the visible DOM
						if (lastMessageElem) {
							offsetTop = lastMessageElem.offsetTop;
						}
					} catch (ex2) {
						offsetTop = 0;
					}

					// SD-2838
                    // Check if the current browser is Mac Computers or iOS Devices
                    if (!this.isMacLike()) {
						// Check if this page is inside an iframe and also a Tile
						if (!!isIFrameLoaded && !!isTilesOpened) {
							// Fix for scrollIntoView() causing the whole page to move, when opened inside a Tile
							element.scroll({ top: offsetTop, behavior: "smooth" });
						} else {
							// continue with default behavior

                            // SD-3057
                            // Fix for Webchat UI Issue when Grammarly extension is installed
							queryLast(".satisfi_messages section.satisfi_user")?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'start'
                            });
						}
					} else {
						// Using `true` here
						// as Safari supports scrollIntoView with boolean parameter, but not smooth behavior option
						//jQuery(".satisfi_messages section:last")[0].scrollIntoView(true);

                        // Current device is an iMac, Macbook, iPhone, iPod, or iPad
                        // custom function for iOS
                        this.scrollToElement(element, offsetTop, 200);
					}

				}
				element.style.height = "500px";
			}, 10);

		} catch (ex) {
			//console.log('Error: ', (ex as Error).message);
		}
    }

    shwoProcessAndTypingMessages() {
      const isIFrameLoaded = this.$store.state.isIFrameLoaded;
			const isTilesOpened = this.$store.state.isTilesOpenedInIframe;
      
      setTimeout(() => {
  
				const element = this.chatBody;
				if (element != null) {

					// Get the offsetTop value for the last message
					let offsetTop = 0;
					try {
						let lastMessageElem = queryLast(".satisfi_messages section", true);
            if (lastMessageElem) {
              if(this.isElementVisible(element, lastMessageElem)) {
                return;
              }
            }

						// Check if an element exists in the visible DOM
						if (lastMessageElem) {
							offsetTop = lastMessageElem.offsetTop;
						}
					} catch (ex2) {
						offsetTop = 0;
					}

					// SD-2838
                    // Check if the current browser is Mac Computers or iOS Devices
                    if (!this.isMacLike()) {
						// Check if this page is inside an iframe and also a Tile
						if (!!isIFrameLoaded && !!isTilesOpened) {
							// Fix for scrollIntoView() causing the whole page to move, when opened inside a Tile
							element.scroll({ top: offsetTop, behavior: "smooth" });
						} else {
							// continue with default behavior

                            // SD-3057
                            // Fix for Webchat UI Issue when Grammarly extension is installed
							queryLast(".satisfi_messages section")?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'start'
                            });
						}
					} else {
						// Using `true` here
						// as Safari supports scrollIntoView with boolean parameter, but not smooth behavior option
						//jQuery(".satisfi_messages section:last")[0].scrollIntoView(true);

                        // Current device is an iMac, Macbook, iPhone, iPod, or iPad
                        // custom function for iOS
                        this.scrollToElement(element, offsetTop, 200);
					}

				}
        
      }, 10);
    }
    
    setScrollSpacerHeight() {
      setTimeout(() => {
        var addonHeight = this.$store.state.lastScrollPosition == "near_top" ?
                                        (document.getElementById("divChatBody").clientHeight * 0.25) : 6;
        this.anchorScrollOffset = Math.max(0,
                                            (document.getElementById("divChatBody").clientHeight) -
                                            queryLast(".satisfi_messages section").clientHeight -
                                            addonHeight
                                          );
        const element = this.chatBody;
        if (element) {
            const threshold = 25;
            const isScrollbarPresent = element.scrollHeight > element.clientHeight;
            const isNotAtBottom = (element.scrollHeight - element.clientHeight - element.scrollTop) > threshold;
            
            var parent = document.getElementById("divChatBody");
            var hasContent = false;
            
            let totalSectionHeight = 0;
            parent.querySelectorAll("section").forEach(section => {
              totalSectionHeight += section.offsetHeight;
            });
            
            hasContent = parent.clientHeight < totalSectionHeight;
          
            if(hasContent && (this.$store.state.lastScrollPosition === "top" || 
                this.$store.state.lastScrollPosition === "near_top")) {
                  
                var lastMessage = Array.from(document.querySelectorAll(".satisfi_bot")).at(-1);
                var addonHeight = this.$store.state.lastScrollPosition == "near_top" ? 
                            (document.getElementById("divChatBody").clientHeight * 0.25) : 6;

                if (!lastMessage) { 
                  hasContent = false;
                }
                else {
                  // Total content height > visible height
                  hasContent =  parent.clientHeight < (lastMessage.clientHeight + addonHeight);  
                }
            } 
            
            // Show button if content exists, scrollbar is present, and user is NOT at the bottom
            if (this.scrollIndicatorTimeout) {
                clearTimeout(this.scrollIndicatorTimeout);
                this.scrollIndicatorTimeout = null;
            }
            
            if (hasContent && isScrollbarPresent && isNotAtBottom) {
                this.scrollIndicatorTimeout = setTimeout(() => {
                    this.showScrollToBottom = true;
                }, 200);
            } else {
                this.showScrollToBottom = false;
            }  
            
        }
      }, 12);
      
    }
    
    scrollChatToBottom(schollSoothly = true) {
		try {
      this.lastProgrammaticScrollTime = Date.now();

      if(this.$store.state.isStreamingPages  && this.$store.state.incomingScrollPosition == "default")
      {
        if(this.$store.state.showTypingIndicator || this.$store.state.showProcessingMessage) {
          this.shwoProcessAndTypingMessages();
        }
        
        return false;
      }

      if (this.scrollIndicatorTimeout) {
          clearTimeout(this.scrollIndicatorTimeout);
          this.scrollIndicatorTimeout = null;
      }
      this.showScrollToBottom = false;
      this.isManualScrollingToBottom = true;
      
			this.resetSpacer = true;
			// Check if we should suppress auto-scrolling (e.g., during message recovery after signalR reconnection)
			if (this.shouldSuppressScrollAndReset()) {
			  //console.log("[SCROLL] return (suppressed)");
			  return;
			}
      
			const element = this.chatBody;
			if (schollSoothly) {
				setTimeout(() => {
					if (element != null) {
						element.scroll({ top: element.scrollHeight, behavior: 'smooth' });
					}
					element.style.height = '500px';
				}, 10);
			}
			else {
				// Check if the system is Mac Computers or iOS Devices
				if(!this.isMacLike()) {
					// Its a non-Mac device
					element.scroll({ top: element.scrollHeight });
				} else {
					// Yes, its either a Mac/iOS Devices
					this.$nextTick(function () {
						element.scrollTop = element.scrollHeight;
					});
				}
			}
		} catch (ex) {
			//console.log('Error: ', (ex as Error).message);
		}
    }
    
    
    scrollChatToBottomMoreIndicator(schollSoothly = true) {
      try {
        this.lastProgrammaticScrollTime = Date.now();
        if (this.scrollIndicatorTimeout) {
            clearTimeout(this.scrollIndicatorTimeout);
            this.scrollIndicatorTimeout = null;
        }
        this.showScrollToBottom = false;
        this.isManualScrollingToBottom = true;
        
        const element = this.chatBody;
        if (schollSoothly) {
          setTimeout(() => {
            if (element != null) {
              element.scroll({ top: element.scrollHeight, behavior: 'smooth' });
            }
            element.style.height = '500px';
          }, 10);
        }
        else {
          // Check if the system is Mac Computers or iOS Devices
          if(!this.isMacLike()) {
            // Its a non-Mac device
            element.scroll({ top: element.scrollHeight });
          } else {
            // Yes, its either a Mac/iOS Devices
            this.$nextTick(function () {
              element.scrollTop = element.scrollHeight;
            });
          }
        }
      } catch (ex) {
        //console.log('Error: ', (ex as Error).message);
      }
    }

    async retryMessage(msgId) {
        const msg: IMessage = this.$store.state.messages.find(
            msg => msg.id === msgId
        );
        try {
            //On retry button click remove retry/cancel link by making status "ok".
            this.$store.commit(STORE_MUTATION.UpdateMessageStatus, {
                id: msgId,
                status: "ok"
            } as IMessage);

            let newMessageResponse;
            //Check failed message is text with button or normal text message.
            if (msg.buttonMessageStructure != null) {
                newMessageResponse = await new ChatService().sendMessage(
                    msg.buttonMessageStructure
                );
                //make it button message structure null if message is send successful.
                this.$store.commit(STORE_MUTATION.UpdateMessageStructure, {
                    id: msgId,
                    buttonMessageStructure: null
                } as IMessage);
            } else {
                newMessageResponse = await new ChatService().sendMessage({
                    content: msg.contents.structures[0].components[0].content,
                    type: "text"
                });
            }

            //On message send update message id from Guid to messageCacheId sent from EBM.
            this.$store.commit(STORE_MUTATION.UpdateMessageCacheID, {
                id: msgId,
                newId: JSON.parse(newMessageResponse).recordInfo.messageCacheId
            } as IMessage);

        } catch (ex) {
            //On message fail make status of that message as "failed".
            this.$store.commit(STORE_MUTATION.SetRequestResponseReceived, true);
            this.$store.commit(STORE_MUTATION.UpdateMessageStatus, {
                id: msgId,
                status: "failed"
            } as IMessage);

            //onExceptionOccured("[chat.ts] An error has occurred -> Inside retryMessage -> Error: ", ex);
        }
    }

    cancelMessage(id) {
        this.$store.commit(STORE_MUTATION.RemoveMessage, id);
        this.$store.commit(STORE_MUTATION.RemoveMessage, id + MESSAGE_STRUCTURE_TYPE.Time);
    }

	/**
	 * Check if the current message is a card type
	 */
	isBotMessageCard(message: IMessage): boolean {
		try {
			// Check if the contents & structures have a valid value
			if (message.contents && message.contents.structures) {
				// Check if any of the structure have a type of "card"
				return message.contents.structures.some((s) => s.type == 'card');
			}
			return false;
		} catch (ex) {}
		return false;
	}

  //#region Methods for handling chat bubble display logic

  /**
   * Gets an array of valid component types supported by the chat interface.
   * @returns {string[]} An array containing the following valid component types
   */
  get validComponentTypes(): string[] {
    return ["text", "html", "htmlblock", "markdown", "button", "link", "image", "line", "break"];
  }

  /**
   * SD-6819: Determines if a message structure should be displayed as a chat bubble.
   * @param structure - The message structure to evaluate
   * @returns {boolean} True if the structure should be shown as a chat bubble, false otherwise
   *
   * @remarks
   * Returns true if:
   * - The structure is a 'bubble' type
   * - An error occurs during evaluation
   *
   * Returns false if:
   * - The structure is invalid/null/undefined
   * - The components array is empty or invalid
   * - Any component has an invalid type
   */
  isShowChatBubble(structure: MessageStructure): boolean {
		try {
			// Check if the structure is valid
			if (!structure || typeof structure !== "object") return false;
			const { components } = structure;

			// Check if the structure is a bubble type and has valid components
			if (!Array.isArray(components) || components.length === 0) return false;

			// Check if all components are invalid
			const allInvalid = components.every((component) => {
				if (!component || typeof component.type !== "string") {
					return true; // Component is invalid or type is not a string
				}
				const typeLowerCase = component.type.toLowerCase();
				const isValidType = this.validComponentTypes.includes(typeLowerCase);
				return !isValidType; // Return true if the type is invalid
			});

			// Hide the bubble if all components or single component is invalid
			return !allInvalid;
		} catch (error) {
			//console.error('Error in isShowChatBubble:', error);
			return true; // Default value
		}
	}

  //#endregion

	/**
	 * JIRA - SD-5915
	 * Determines if the provided message object is a valid carousel or list layout.
	 *
	 * JIRA - SD-7651
	 * Checks for layout = "carousel" or "list" and component.type === "carousel" (To exclude the bubble message on top of carousel)
	 *
	 * @param {IMessage} message - The message object to validate.
	 * @param {MessageStructure} structure - The messageStructure object to validate.
	 * @returns {boolean} `true` if the message is valid and has a layout of either 'carousel' or 'list', otherwise `false`.
	 * @throws Logs an error to the console if an exception occurs during validation.
	 */
	isValidCarousel(message: IMessage, structure: MessageStructure) {
		let isValid = false
		try {
			isValid =  message && message.contents && message.contents.layout && (message.contents.layout=='carousel' || message.contents.layout=='list')
					  && structure && Array.isArray(structure.components) && structure.components.some(component => !Util.isNullOrEmpty(component.type) && component.type === "carousel")
		} catch (error) {
			console.error("'ClientApp/chat.ts' - Error while parsing the message object to valid Carousel. message = ", message)
		}

		return isValid
	}

  /**
   * SD-6270: Returns the alt label for the avatar based on the message type.
   * Reference: https://www.davidmacd.com/blog/alternate-text-for-css-background-images.html
   *
   * @param {MESSAGE_TYPE} messageType - The type of the message.
   * @returns {string} The alt label for the avatar.
   */
  getAvatarAltLabel(messageType: MESSAGE_TYPE): string {
		try {
			switch (messageType) {
				case MESSAGE_TYPE.Bot:
					return "AI Agent Avatar";
				case MESSAGE_TYPE.Agent:
					return "Live Agent Avatar";
				default:
					return ""; // Handle other message types as needed
			}
		} catch (error) {
			//console.error('Error occurred:', error);
			return "";
		}
	}

  /**
   * Determines if an alternative label for the avatar is required based on the message type and avatar settings.
   *
   * @param {IMessage} message - The message object to evaluate.
   * @returns {boolean} - Returns `true` if the avatar alternative label is required, otherwise `false`.
   *
   * The function checks if the message is valid and then evaluates whether the bot or agent avatar is enabled.
   * It returns `true` if either the bot avatar is enabled for a bot message or the agent avatar is enabled for a user message.
   * If an error occurs during the evaluation, it catches the exception and returns `false`.
   */
  isAvatarAltLabelRequired(message: IMessage): boolean {
    try {
      // Check if the message is valid
      if (!message) return false;

      // Check if the bot or agent avatar is enabled
      const { isBotHasAvatar, isUserHasAvatar } = this.$store.state;
      const showBotAvatar = isBotHasAvatar && message.type==0 && !this.isBotMessageCard(message);
      const showAgentAvatar = isUserHasAvatar && message.type==3;
      const isRequired = showBotAvatar || showAgentAvatar;
      return !!isRequired;
    } catch (ex) {
      //console.error('Error occurred:', ex);
      return false;
    }
  }

  async onMessageLinkClick(event: MouseEvent) {
    const link = (event.target as HTMLElement)?.closest("a");
    if (!link) return;

    const href = link.getAttribute("href");
    if (!href || href.trim() === "") return;

    // SD-12696: intercept satis.fi/iframe special URLs and render inline
    if (/^https?:\/\/satis\.fi\/iframe\//i.test(href)) {
        event.preventDefault();
        handleSpecialIframeUrl(href);
        return;
    }
    
    const target = event.target as HTMLAnchorElement;
    if (target && target.tagName === 'A' && target.href) {
        const chatService = new ChatService();
        
        // Normalize the href by removing protocol decorators and lowercasing it
        const targetUrl = UrlUtils.normalizeUrl(target.href);
  
        // Retrieve the base action URL from the Vuex store
        const baseActionUrl = this.$store.state.baseInlineActionUrl || "";
        if (!baseActionUrl) return;
  
        // Normalize and compare URLs to determine if it's a handled action link
        const isActionUrl = targetUrl.startsWith(baseActionUrl.toLowerCase());
        if (!!isActionUrl) return;
        
        // Post the inline click action to the server
        await chatService.inlineClickTracking(target.textContent || '', target.href);
        console.warn("Link clicked:", target.textContent, target.href);
    }
    
  }

	//#region Message Feedback

	/**
	 * Determine whether feedback UI (rating) should be shown for a message.
	 *
	 * Rules:
	 * - Only show feedback when `message.contents.settings.feedback.enableRating === true`.
	 * - Return `false` on any error or missing data.
	 *
	 * @param message - The IMessage object to check.
	 * @returns boolean - true if feedback UI should be shown, false otherwise.
	 */
	shouldShowFeedback(message?: IMessage): boolean {
		try {
			// Quick guard: must be an object
			if (!Util.isPlainObject(message)) return false;

			// `contents` expected to be an object per MessageContent type
			const contents = (message as IMessage).contents;
			if (!Util.isPlainObject(contents)) return false;

			// `settings` may be missing; guard it
			const settings = (contents as { settings?: MessageContentSettings }).settings;
			if (!Util.isPlainObject(settings)) return false;

			// `feedback` may be missing; guard it
			const feedback = (settings as MessageContentSettings).feedback;
			if (!Util.isPlainObject(feedback)) return false;

			// Only show feedback when enableRating is explicitly boolean true.
			// Avoids treating string "true" or other truthy values as enabled.
			return (feedback as { enableRating?: unknown }).enableRating === true;
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.error("[chat.ts] Error occurred while checking feedback visibility", ex);
			return false; // feedback not shown
		}
	}

	/**
   * Determines if feedback is in cooldown and user is not allowed to provide feedback.
   * @returns True if cooling down.
   */
	isFeedbackInCooldown(messageId: string): boolean {
		try {
			if (!messageId) return false;
			const cooldown = this.$store.state.feedbackCooldowns ?? {};
			if (typeof cooldown !== "object" || cooldown === null || !(messageId in cooldown)) return false;
			const cooldownEntry = cooldown[messageId];
			if (!cooldownEntry || !Util.isValidCooldownExpiry(cooldownEntry.expiry)) return false;
			return Date.now() < cooldownEntry.expiry;
		} catch {
			return false;
		}
	}

	/**
	 * Determines if a message currently has a pending rating operation.
	 * Use this to prevent duplicate requests and race conditions.
	 *
	 * @param message - The message to check for pending rating
	 * @returns `true` if the message has a pending rating, `false` otherwise
	 */
	isRatingPending(message: IMessage): boolean {
		try {
			if (!message) return false;
			return message?.feedback?.isRatingPending || false;
		} catch {
			return false;
		}
	}

	/**
	 * Retrieves the feedback rating for a given message.
	 *
	 * @param message - The message object to get the rating from
	 * @returns The feedback rating of the message, or FeedbackRating.Cleared if the message is null.
	 */
	currentMessageRating(message: IMessage): FeedbackRating {
		try {
			if (!message) return FeedbackRating.Cleared;
			return message?.feedback?.rating || FeedbackRating.Cleared;
		} catch {
			return FeedbackRating.Cleared;
		}
	}

  /**
   * Handles opening the feedback modal when thumbs down is clicked
   * @param data - Message data from the feedback component
   */
  handleShowFeedbackModal(data: any) {
    try {
      this.feedbackModalData = data;
      this.showFeedbackModal = true;
    } catch (ex) {
      console.error("[chat.ts] Error opening feedback modal:", ex);
    }
  }

  /**
   * Handles feedback modal close
   */
  handleHideFeedbackModal() {
    this.showFeedbackModal = false;
    this.feedbackModalData = null;
  }

	@Watch("showFeedbackModal", { immediate: true })
	onFeedbackModalChanged(value: any) {
		this.handleShowTranscriptModalChange(value);
	}

	//#endregion
    
    //#region Accessibility Watchers

    /**
     * JIRA SD-10864: Watches the messages array for new entries.
     * Uses deep: true to detect nested changes. Logic ensures only unique, 
     * new messages trigger a screen reader announcement.
     * * @param newVal - The updated array of chat messages.
     */
    @Watch("messages.length", { immediate: true })
    onMessageCountChanged() {
        try {
            if (this.screenReaderMessageTimeout) {
                clearTimeout(this.screenReaderMessageTimeout);
            }
            
            this.$nextTick(() => {
                                  // Update scroll to bottom button visibility when messages change
                    const element = this.chatBody;
                    if (element) {
                        const threshold = 25;
                        const isScrollbarPresent = element.scrollHeight > element.clientHeight;
                        const isNotAtBottom = (element.scrollHeight - element.clientHeight - element.scrollTop) > threshold;
                        
                        var parent = document.getElementById("divChatBody");
                        var hasContent = false;
                        
                        let totalSectionHeight = 0;
                        parent.querySelectorAll("section").forEach(section => {
                          totalSectionHeight += section.offsetHeight;
                        });
                        
                        hasContent = parent.clientHeight < totalSectionHeight;
                        
                        if(hasContent && (this.$store.state.lastScrollPosition === "top" || 
                            this.$store.state.lastScrollPosition === "near_top")) {
                              
                            var lastMessage = Array.from(document.querySelectorAll(".satisfi_bot")).at(-1);
                            var addonHeight = this.$store.state.lastScrollPosition == "near_top" ? 
                                        (document.getElementById("divChatBody").clientHeight * 0.25) : 6;

                            if (!lastMessage) { 
                              hasContent = false;
                            }
                            else {
                              // Total content height > visible height
                              hasContent =  parent.clientHeight < (lastMessage.clientHeight + addonHeight);  
                            }
                        } 
                        
                        // Show button if content exists, scrollbar is present, and user is NOT at the bottom
                        if (this.scrollIndicatorTimeout) {
                            clearTimeout(this.scrollIndicatorTimeout);
                            this.scrollIndicatorTimeout = null;
                        }
                        
                        if (hasContent && isScrollbarPresent && isNotAtBottom) {
                            this.scrollIndicatorTimeout = setTimeout(() => {
                                this.showScrollToBottom = true;
                            }, 200);
                        } else {
                            this.showScrollToBottom = false;
                        }  
                    }

                    this.screenReaderMessageTimeout = setTimeout(() => {
                        const messageElements = this.$refs.messageRefs as HTMLElement[];
                        
                        if (messageElements && messageElements.length > 0) {
                            // Last message to announce; may be nulled below if it's also the welcome message                                                    
                            const isStreamingMessage = this.messages[this.messages.length - 1]?.contents?.keys?.streamingMessageId;
                            let lastMessageEl = messageElements[messageElements.length - 1];
                            
                            if (isStreamingMessage || (!lastMessageEl?.classList.contains(this.getClassName("bot")) && !lastMessageEl?.classList.contains(this.getClassName("user")))){
                              lastMessageEl = null
                            }
                            
                            const elementsToRead: (HTMLElement | string)[] = [];

                            let welcomeMessage = null

                            // Capture welcome message once; if it's the only message, don't double-read it as last
                            if(!this.isWelcomeRead && messageElements[0]?.classList.contains(this.getClassName("bot"))) {
                              welcomeMessage = messageElements[0];

                              if (messageElements.length === 1 && lastMessageEl.classList.contains(this.getClassName("bot"))) {
                                lastMessageEl = null
                              }
                            }

                            // Queue welcome message first, mark it read to avoid re-announcing
                            if (welcomeMessage) {
                               // Prepend page title so screen readers identify the chat on first load
                              if (!this.isTitleRead && document.title) {
                                  elementsToRead.push(document.title + " ")
                                  this.isTitleRead = true
                              }
                              
                              elementsToRead.push(welcomeMessage);
                              this.isWelcomeRead = true;

                              // Prepend page title so screen readers identify the chat on first load
                              if (!this.isTitleRead && document.title) {
                                  elementsToRead.push(document.title + " ")
                                  this.isTitleRead = true
                              }
                            }

                            // Queue the latest bot message for announcement
                            if(lastMessageEl){
                              elementsToRead.push(lastMessageEl);
                            }

                            DynamicAccessibilityHandler.announceElementContent(elementsToRead);
                        }
                    }, 1000); // Some of the quick replies are rendering with little delay. To address this we are delaying
                });
            
            } catch (error) {
            console.error("[chat.ts] Error in onMessageCountChanged:", error);
        }
    }
    
    /**
     * Triggers scroll/screen-reader logic when streaming completes.
     * `onMessageCountChanged` doesn't fire during streaming (message count
     * doesn't change), so this catches the true→false transition instead.
     */
    @Watch("lastStreamedMessageCount")
    onlastStreamedMessageCountChanged(value: string) {
      if (value) {
        
        if (this.streaminMessageTimeout) {
          clearTimeout(this.streaminMessageTimeout);
        }
        
        this.streaminMessageTimeout = setTimeout(() => {
          
          const messageElements = this.$refs.messageRefs as HTMLElement[];
          const streamedMessageIndex = this.messages.findLastIndex(({contents}) => contents?.keys?.streamingMessageId)
          
          if (streamedMessageIndex >= 0) {
            const streamedMessageEl = messageElements[streamedMessageIndex];
            
            if (streamedMessageEl) {
              DynamicAccessibilityHandler.announceElementContent([streamedMessageEl]);
            }
          }
          
        }, 2000);
      }
    }
    
    /** 
     * Watches for direct screen reader announcement requests.
     * Updates the aria-live region with a slight delay to ensure screen readers
     * detect the change even if the same text is announced consecutively.
     * * @param value - The plain text string to be spoken by the screen reader.
     */
    @Watch("screenReaderAnnouncement", { immediate: true }) 
    onScreenReaderAnnouncementChanged(value?: string) {
        /**
         * Updates the hidden aria-live region.
         * Uses a 100ms timeout to ensure the DOM has stabilized and the 
         * screen reader's buffer correctly registers the text change.
         * * @param text - The plain text string to be spoken by the screen reader.
         */
        
        /* Clear timeout if exists */
        if (this.screenReaderTimeout) {
            clearTimeout(this.screenReaderTimeout);
        }
        
        this.screenReaderTimeout = setTimeout(() => {
            this.a11yAnnouncement = value;
        }, 100);
    }

    /**
     * JIRA SD-10864: Monitors the typing indicator state to provide 
     * real-time status updates to screen reader users.
     * * @param val - Boolean indicating if the bot is currently typing.
     */
    @Watch("showTypingIndicator")
    onTypingChanged(val: boolean) {
        if (val) {
            DynamicAccessibilityHandler.announcementContent("Bot is typing...");
        }
    }

    /**
     * JIRA SD-10864: Monitors the processing message state (e.g., long-running API calls)
     * to ensure the user is aware the bot is still active.
     * * @param val - String indicating the processing text.
     */
    @Watch("processingDisplayText")
    onProcessingChanged(val: string) {
        if (val) {
            DynamicAccessibilityHandler.announcementContent(val);
        }
    }

    beforeDestroy() {
        if (this.screenReaderTimeout) {
            clearTimeout(this.screenReaderTimeout);
        }
        
        if (this.screenReaderMessageTimeout) {
            clearTimeout(this.screenReaderMessageTimeout);
        }
        
        if (this.inertInterval) {
            clearInterval(this.inertInterval);
        }
    }
  
    /**
     * Toggles the 'inert' state of background elements when a modal is visible.
     * * This watcher ensures a proper focus trap by making the background (header, footer, and chat) 
     * non-interactive for both keyboard and screen reader users. It includes a defensive 
     * check for parent document access to handle Cross-Origin security restrictions 
     * when running inside an iframe.
     * * @param {boolean} isVisible - The current visibility state of the modal or popup.
     */
    @Watch("isModalVisible")
    onModalVisible(isVisible: boolean) {
      const toggleElements = [
        document.getElementById("satisfiHeaderContainer"),
        document.getElementById("satisfiChatContainer"),
        document.getElementById("satisfiFooterContainer"),
        document.getElementById("satisfiTaglineContainer")
      ];
      toggleInertForSiblings(toggleElements, isVisible, this.inertInterval, this.isPopupIframe);
      
      // Reset announcement logic
      if (isVisible) {
        this.a11yAnnouncement = "";
      }
    }
    //#endregion

	//#region Sender Display Methods

  /**
   * Determines if sender information can be shown for a message based on its type and whether it's a card message.
   *
   * @param message - The message to check
   * @returns true if: (type==0 or type==3) AND NOT a card message
   */
  canShowSenderByType(message: IMessage): boolean {
    try {
      if (!message) return false;
      // Only show sender info for Bot (0) and Agent (3) messages, and exclude card messages
      const isAgentMessage = message.type === MESSAGE_TYPE.Bot || message.type === MESSAGE_TYPE.Agent;
      const isNotCard = !this.isBotMessageCard(message);
      return isAgentMessage && isNotCard;
    } catch {
      return false;
    }
  }

  /**
   * Determines whether the sender attribution row should be rendered for a message at all.
   *
   * @param message - The message to check
   * @returns true if canShowSenderByType passes AND at least one of the avatar or the name
   *          resolves to something showable - the row is dropped only when BOTH are absent
   */
  shouldShowSenderDisplay(message: IMessage): boolean {
    try {
      if (!this.canShowSenderByType(message)) return false;

      const showAvatar = this.shouldShowSenderAvatar(message);
      const showName = this.shouldShowSenderName(message);

      // hide SenderDisplay only when BOTH are false
      return showAvatar || showName;
    } catch (ex) {
      return false;
    }
  }

  /**
   * JIRA: SD-13645 (feedback round 2)
   * True when this message renders as a horizontal carousel.
   *
   * Such messages render their own attribution row inside LayoutCarousel, because the
   * approved design puts the navigation arrows on that same line. chat.ts therefore
   * suppresses its own copy of the row for them - see the template - while still owning
   * the resolution rules and handing the result down as props.
   *
   * Deliberately narrower than isValidCarousel, which also accepts the "list" layout: a list
   * renders as a vertical stack with no arrows, so its attribution stays where it is.
   */
  isCarouselMessage(message: IMessage): boolean {
    try {
      if (!message || !message.contents || message.contents.layout !== "carousel") return false;

      const structures = message.contents.structures;

      return Array.isArray(structures) && structures.some((structure) => this.isValidCarousel(message, structure));
    } catch (ex) {
      return false;
    }
  }

  /**
   * Gets the display value based on mode, custom value, and default value.
   *
   * @param mode - The display mode ("hide", "custom", or "default")
   * @param customValue - The custom value to use if mode is "custom"
   * @param defaultValue - The default value to use if mode is "default"
   * @returns The resolved display value, or empty string if hidden
   */
  getDisplayValue(mode: string, customValue: string, defaultValue: string): string {
    if (mode === "hide") return "";
    if (mode === "custom") return customValue || "";
    return defaultValue || "";
  }
  
  /**
   * Normalizes a string value by trimming whitespace.
   */
  normalizeStr(value: unknown): string {
    return Util.trimString(value);
  }
  
  /**
   * Normalizes the display mode value to ensure it's one of the accepted values ("default", "custom", "hide").
   * If the input is invalid, it defaults to "default".
   */
  normalizeDisplayMode(mode: unknown): SenderDisplayMode {
    try {
      if (typeof mode !== "string") return "default";
      const normalized = mode.trim().toLowerCase() as SenderDisplayMode;
      return normalized === "default" || normalized === "custom" || normalized === "hide" ? normalized : "default";
    } catch (ex) {
      return "default";
    }
  }
  
  /**
   * Normalizes the sender display settings object, ensuring all properties are valid and have default fallbacks.
   * If the input is not a valid object, it returns an empty settings object.
   */
  normalizeSettings(settings: unknown): SenderDisplaySettingsConfig {
    try {
      if (!Util.isPlainObject(settings)) return {};
      const s = settings as SenderDisplaySettingsConfig;
      return {
        senderDisplay: this.normalizeDisplayMode(s.senderDisplay),
        avatarDisplay: this.normalizeDisplayMode(s.avatarDisplay),
        customSenderName: this.normalizeStr(s.customSenderName),
        customAvatarUrl: this.normalizeStr(s.customAvatarUrl)
      };
    } catch (ex) {
      return {};
    }
  }

	/**
	 * Resolves sender display settings with defaults applied.
	 *
	 * @param message - The message containing settings
	 * @returns ResolvedSenderSettings with all values resolved
	 */
	resolveSenderDisplaySettings(message: IMessage): ResolvedSenderSettings {
		try {
			if (!message || !message.contents) {
				return { senderName: "", avatarUrl: "", showAvatar: false, showName: false };
			}

			const settings = this.normalizeSettings(message?.contents?.settings?.senderDisplaySettings);
			const senderDisplayMode: SenderDisplayMode = settings.senderDisplay || "default";
			const avatarDisplayMode: AvatarDisplayMode = settings.avatarDisplay || "default";

			// Resolve sender name
			const defaultSenderName = this.normalizeStr(this.getDefaultSenderName(message.type));
			const senderName = this.getDisplayValue(senderDisplayMode, settings.customSenderName, defaultSenderName);

			// Resolve avatar URL
			const defaultAvatarUrl = this.normalizeStr(this.getDefaultAvatarUrl(message.type));
			const avatarUrl = this.getDisplayValue(avatarDisplayMode, settings.customAvatarUrl, defaultAvatarUrl);

			// Determine visibility flags
			const showAvatar = !Util.isNullOrEmpty(avatarUrl);
			const showName = !Util.isNullOrEmpty(senderName);

			return { senderName, avatarUrl, showAvatar, showName };
		} catch (ex) {
			return { senderName: "", avatarUrl: "", showAvatar: false, showName: false };
		}
	}

	/**
	 * Gets the sender name to display.
	 *
	 * @param message - The message containing sender settings
	 * @returns The resolved sender name, or empty string if hidden
	 */
	getSenderDisplayName(message: IMessage): string {
		try {
			const resolved = this.resolveSenderDisplaySettings(message);
			return resolved.senderName;
		} catch (ex) {
			return "";
		}
	}

	/**
	 * Gets the avatar URL to display.
	 *
	 * @param message - The message containing avatar settings
	 * @returns The resolved avatar URL, or empty string if hidden
	 */
	getSenderDisplayAvatarUrl(message: IMessage): string {
		try {
			const resolved = this.resolveSenderDisplaySettings(message);
			return resolved.avatarUrl;
		} catch (ex) {
			return "";
		}
	}

	/**
	 * Gets the default avatar URL based on message type from store.
	 *
	 * @param messageType - The message type (0=AI Agent, 3=Live Agent)
	 * @returns The avatar URL, or empty string if not available
	 */
	getDefaultAvatarUrl(messageType: number): string {
		try {
			if (messageType === MESSAGE_TYPE.Bot) {
				return this.$store.state.aiAgentAvatarUrl || "";
			} else if (messageType === MESSAGE_TYPE.Agent) {
				return this.$store.state.liveAgentAvatarUrl || "";
			}
			return "";
		} catch (ex) {
			return "";
		}
	}
  
  /**
   * Gets the default sender name based on message type from store.
   */
	getDefaultSenderName(messageType: number): string {
		try {
			if (messageType === MESSAGE_TYPE.Bot) {
				return this.$store.state.senderDisplayName || "";
			} else if (messageType === MESSAGE_TYPE.Agent) {
				return this.$store.state.senderLiveDisplayName || "";
			}
			return "";
		} catch (ex) {
			return "";
		}
	}

	/**
	 * Determines if sender avatar should be displayed.
	 *
	 * @param message - The message containing avatar settings
	 * @returns True if avatar URL is available, false otherwise
	 */
	shouldShowSenderAvatar(message: IMessage): boolean {
		try {
			const avatarUrl = this.getSenderDisplayAvatarUrl(message);
			return !Util.isNullOrEmpty(avatarUrl);
		} catch (ex) {
			return false;
		}
	}

	/**
	 * Determines if sender name should be displayed.
	 *
	 * @param message - The message containing sender name settings
	 * @returns True if sender name is available, false otherwise
	 */
	shouldShowSenderName(message: IMessage): boolean {
		try {
			const senderName = this.getSenderDisplayName(message);
			return !Util.isNullOrEmpty(senderName);
		} catch (ex) {
			return false;
		}
	}

	//#endregion
}
